import React from "react";
import { Modal, Form, InputNumber, Input, DatePicker, Row, Col } from "antd";
import dayjs from "dayjs";
import {
  rateNotAboveMrpRules,
  RATE_FIELDS,
  gstRateRules,
  revalidateGstRates,
} from "../common/validators";
import readOnlyStyles from "../common/readOnlyField.module.css";


const BATCH_NUMBER_MAX = 30;

/*
 * The IGST a batch opens with.
 *
 * It is the pair added up wherever a pair was stored, so a batch seeded
 * before the two were tied together opens already agreeing with itself rather
 * than opening on a figure nobody can reach - the box is read-only, and a
 * stale IGST would otherwise have to be dislodged by retyping a rate that was
 * already right. A batch carrying IGST and no pair at all is left as it is,
 * for the rate to be written out the way the rest of the module writes it.
 */
const openingIgst = (values) => {
  const sgst = Number(values?.sgst) || 0;
  const cgst = Number(values?.cgst) || 0;
  if (!sgst && !cgst) return Number(values?.igst) || 0;
  return Number((sgst + cgst).toFixed(2));
};

const BatchDetailsModal = ({ open, setOpen, initialValues, onSave }) => {
  const [form] = Form.useForm();

  /*
   * IGST is the pair added up, not a third figure to be typed.
   *
   * The medicine this batch was seeded from works the same way, and a batch
   * that was free to disagree with it is how a line came to be taxed at
   * SGST 3 + CGST 4 + IGST 3 - three different answers to one question. The
   * pair is still typed, and still has to be a pair: the equality is asked
   * about rather than forced, because the form cannot know which of the two
   * boxes is the wrong one.
   */
  const syncIgst = (changed) => {
    const read = (field) =>
      Number(changed[field] !== undefined ? changed[field] : form.getFieldValue(field));
    const sgst = read("sgst");
    const cgst = read("cgst");
    const total =
      (Number.isFinite(sgst) ? sgst : 0) + (Number.isFinite(cgst) ? cgst : 0);
    form.setFieldValue("igst", Number(total.toFixed(2)));
    revalidateGstRates(form);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSave({
          ...values,
          batch_number: (values.batch_number ?? "").trim(),
          expiry: values.expiry.endOf("month").format("YYYY-MM-DD"),
          manufacturing_date: values.manufacturing_date
            ? values.manufacturing_date.format("YYYY-MM-DD")
            : null,
        });
        setOpen(false);
      })
      .catch(() => {

      });
  };

  return (
    <Modal
      title="Batch Details"
      open={open}
      onCancel={() => setOpen(false)}
      onOk={handleOk}
      centered
      width={520}
      okText="Save Batch"
      destroyOnClose
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          batch_number: initialValues?.batch_number,
          expiry: initialValues?.expiry ? dayjs(initialValues.expiry) : null,
          manufacturing_date: initialValues?.manufacturing_date ? dayjs(initialValues.manufacturing_date) : null,
          mrp: initialValues?.mrp ?? 0,
          selling_price: initialValues?.selling_price ?? 0,
          rate_b: initialValues?.rate_b ?? 0,
          rate_c: initialValues?.rate_c ?? 0,
          sgst: initialValues?.sgst ?? 0,
          cgst: initialValues?.cgst ?? 0,
          igst: openingIgst(initialValues),
          cess: initialValues?.cess ?? 0,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Batch Number"
              name="batch_number"
              rules={[
                {
                  required: true,
                  message: "Please enter batch number",
                  transform: (value) => (value ?? "").trim(),
                },
                {
                  validator: (_, value) => {
                    const text = (value ?? "").trim();
                    if (!text) return Promise.resolve();
                    if (text.length > BATCH_NUMBER_MAX) {
                      return Promise.reject(
                        new Error(
                          `Batch number cannot exceed ${BATCH_NUMBER_MAX} characters`
                        )
                      );
                    }
                    // Manufacturer batch/lot codes come in formats like
                    // ABC123, 123456, ABC-123, ABC/123 and ABC 123.
                    if (!/^[A-Za-z0-9 /-]+$/.test(text)) {
                      return Promise.reject(
                        new Error(
                          "Batch number can contain letters, numbers, spaces, - and / only"
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="Batch Number" maxLength={BATCH_NUMBER_MAX} />
            </Form.Item>
          </Col>
          <Col span={12}>
            {/*
              * A medicine expires at the end of a month, not on a day of it -
              * that is what the pack prints and what the pharmacist reads off
              * it. The month is picked and the date it is kept as is that
              * month's last day, so nothing downstream has to guess.
              */}
            <Form.Item label="Expiry" name="expiry" rules={[{ required: true, message: "Please select expiry" }]}>
              <DatePicker
                picker="month"
                format="MMM YYYY"
                placeholder="Select month"
                style={{ width: "100%" }}
                disabledDate={(d) => d && d.endOf("month") < dayjs().endOf("day")}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Manufacturing Date" name="manufacturing_date">
              <DatePicker style={{ width: "100%" }} disabledDate={(d) => d && d > dayjs().endOf("day")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="MRP" name="mrp" rules={[{ required: true, message: "Required" }]}>
              <InputNumber style={{ width: "100%" }} min={0}
                onChange={() =>
                  form.validateFields(RATE_FIELDS).catch(() => {})
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Selling Price (Rate A)" name="selling_price" rules={[
              ...rateNotAboveMrpRules(form, "Selling price"),
              { required: true, message: "Required" }]}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Rate B" name="rate_b"
              rules={rateNotAboveMrpRules(form, "Rate B")}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Rate C" name="rate_c"
              rules={rateNotAboveMrpRules(form, "Rate C")}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="SGST %" name="sgst" rules={gstRateRules(form, "sgst")}>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                onChange={(value) => syncIgst({ sgst: value })}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="CGST %" name="cgst" rules={gstRateRules(form, "cgst")}>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                onChange={(value) => syncIgst({ cgst: value })}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="IGST %" name="igst" rules={gstRateRules(form, "igst")}>
              {/* Read-only with the steppers taken away: antd leaves the
                  arrows live on a read-only InputNumber, so a box that
                  refuses to be typed in could still be clicked off the sum. */}
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                readOnly
                controls={false}
                className={readOnlyStyles.readOnly}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Cess %" name="cess">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default BatchDetailsModal;
